import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import { fetchPauseReasons } from '../../services/callcenterV2/pauseReasonsService';
import { extractExtension, isWebEndpoint, displayQueue, computeQueuePrefix } from './utils';
import { getDispositionBadge } from './dispositionBadge';
import { formatDuration, formatDateTimeSeconds } from '../../utils/datetime';
import { PERIODS, PERIOD_TODAY, PERIOD_CUSTOM, periodToRange } from '../../services/callcenterV2/periodPresets';
import './config-styles.css';

const getApiBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

const authHeaders = (token, tenantId) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  'x-tenant-id': tenantId,
});

const toNum = (value) => Number(value) || 0;

const PAUSES_POLL_INTERVAL_MS = 30000;

function aggregateQueues(rows) {
  const byQueue = new Map();
  for (const row of rows) {
    const key = row.queue_key || row.queue_name;
    if (!key) continue;
    const acc = byQueue.get(key) || {
      queue_key: key,
      queue_name: row.queue_name || key,
      calls_offered: 0,
      calls_answered: 0,
      calls_noanswer: 0,
      calls_abandoned: 0,
      calls_abandoned_total: 0,
      calls_short_abandon: 0,
      calls_answered_within_sl: 0,
      total_wait_time: 0,
      total_talk_time: 0,
    };
    acc.calls_offered += toNum(row.calls_offered);
    acc.calls_answered += toNum(row.calls_answered);
    acc.calls_noanswer += toNum(row.calls_noanswer);
    acc.calls_abandoned += toNum(row.calls_abandoned);
    acc.calls_abandoned_total += toNum(row.calls_abandoned_total);
    acc.calls_short_abandon += toNum(row.calls_short_abandon);
    acc.calls_answered_within_sl += toNum(row.calls_answered_within_sl);
    acc.total_wait_time += toNum(row.total_wait_time);
    acc.total_talk_time += toNum(row.total_talk_time);
    byQueue.set(key, acc);
  }
  return Array.from(byQueue.values()).map((q) => {
    const resolved = q.calls_answered + q.calls_abandoned_total;
    const offeredEffective = q.calls_offered - q.calls_short_abandon;
    return {
      ...q,
      asa: q.calls_answered > 0 ? q.total_wait_time / q.calls_answered : 0,
      aht: q.calls_answered > 0 ? q.total_talk_time / q.calls_answered : 0,
      abandon_rate: resolved > 0 ? (q.calls_abandoned_total / resolved) * 100 : 0,
      service_level: offeredEffective > 0 ? (q.calls_answered_within_sl / offeredEffective) * 100 : 0,
    };
  });
}

function PeriodFilters({ period, startDate, endDate, onPeriodChange, onDateChange }) {
  return (
    <>
      <div className="ccv2-filter-group">
        <label>Período</label>
        <select value={period} onChange={(e) => onPeriodChange(e.target.value)}>
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
          <option value={PERIOD_CUSTOM}>Personalizado</option>
        </select>
      </div>
      <div className="ccv2-filter-group">
        <label>Data Início</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onDateChange('startDate', e.target.value)}
        />
      </div>
      <div className="ccv2-filter-group">
        <label>Data Fim</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onDateChange('endDate', e.target.value)}
        />
      </div>
    </>
  );
}

function Reports() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calls, setCalls] = useState([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [metrics, setMetrics] = useState({ queueMetrics: [], agentMetrics: [] });
  const [activeTab, setActiveTab] = useState('calls');
  const [tenantId, setTenantId] = useState('');
  const [tenantPrefix, setTenantPrefix] = useState(null);
  const [queues, setQueues] = useState([]);
  const [pauses, setPauses] = useState([]);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [period, setPeriod] = useState(PERIOD_TODAY);
  const [filters, setFilters] = useState(() => {
    const { startDate, endDate } = periodToRange(PERIOD_TODAY);
    return { startDate, endDate, queue: '', status: '', agent: '', reason: '' };
  });

  const applyPeriod = useCallback((value) => {
    setPeriod(value);
    if (value === PERIOD_CUSTOM) return;
    const { startDate, endDate } = periodToRange(value);
    setFilters((prev) => ({ ...prev, startDate, endDate }));
  }, []);

  const setDateField = useCallback((field, value) => {
    setPeriod(PERIOD_CUSTOM);
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const fetchSeq = useRef(0);

  const fetchQueues = useCallback(async () => {
    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      setTenantId(tid);
      const token = await getToken();
      const response = await fetch(`${apiBase}/v2/callcenter/queues`, {
        headers: authHeaders(token, tid),
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.queues)
            ? data.queues
            : Array.isArray(data?.data)
              ? data.data
              : (data && typeof data === 'object')
                ? Object.values(data)
                : [];
        setQueues(list);
        setTenantPrefix(data?.namePrefix ?? null);
      }
    } catch (e) {
      console.error('Erro ao buscar filas:', e);
    }
  }, []);

  const fetchCalls = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      setError(null);
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });
      if (filters.queue) params.append('queue_key', filters.queue);
      if (filters.status) params.append('disposition', filters.status);

      const response = await fetch(`${apiBase}/v2/analytics/history/${tid}/calls?${params}`, {
        headers: authHeaders(token, tid),
      });
      if (seq !== fetchSeq.current) return;

      if (response.ok) {
        const data = await response.json();
        setCalls(data.data || []);
        setCallsTotal(data.total || 0);
      } else {
        setError('Falha ao carregar chamadas');
      }
    } catch (e) {
      if (seq === fetchSeq.current) setError('Falha ao carregar chamadas');
      console.error('Erro ao buscar chamadas:', e);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [filters]);

  const fetchMetrics = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      setError(null);
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();
      const headers = authHeaders(token, tid);

      const queueParams = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });

      const [queuesRes, agentsRes] = await Promise.all([
        fetch(`${apiBase}/v2/analytics/history/${tid}/queues?${queueParams}`, { headers }),
        fetch(`${apiBase}/v2/analytics/history/${tid}/agents?${queueParams}`, { headers }),
      ]);
      if (seq !== fetchSeq.current) return;

      const queueData = queuesRes.ok ? await queuesRes.json() : { data: [] };
      const agentData = agentsRes.ok ? await agentsRes.json() : { data: [] };
      if (!queuesRes.ok || !agentsRes.ok) setError('Falha ao carregar métricas');

      setMetrics({
        queueMetrics: aggregateQueues(queueData.data || []),
        agentMetrics: agentData.data || [],
      });
    } catch (e) {
      if (seq === fetchSeq.current) setError('Falha ao carregar métricas');
      console.error('Erro ao buscar métricas:', e);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [filters]);

  const fetchPauses = useCallback(async (silent = false) => {
    const seq = ++fetchSeq.current;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.reason) params.append('reason_code', filters.reason);

      const response = await fetch(`${apiBase}/v2/analytics/history/${tid}/pauses?${params}`, {
        headers: authHeaders(token, tid),
      });
      if (seq !== fetchSeq.current) return;

      if (response.ok) {
        const data = await response.json();
        setPauses(data.data || []);
      } else {
        setError('Falha ao carregar pausas');
      }
    } catch (e) {
      if (seq === fetchSeq.current) setError('Falha ao carregar pausas');
      console.error('Erro ao buscar pausas:', e);
    } finally {
      if (seq === fetchSeq.current && !silent) setLoading(false);
    }
  }, [filters]);

  const downloadPausesCsv = useCallback(async () => {
    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const token = await getToken();

      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.reason) params.append('reason_code', filters.reason);

      const response = await fetch(`${apiBase}/v2/analytics/export/pauses/${tid}?${params}`, {
        headers: authHeaders(token, tid),
      });
      if (!response.ok) {
        setError('Falha ao exportar CSV de pausas');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pausas_${tid}_${filters.startDate}_${filters.endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Falha ao exportar CSV de pausas');
      console.error('Erro ao exportar pausas:', e);
    }
  }, [filters]);

  const loadPauseReasons = useCallback(async () => {
    try {
      const reasons = await fetchPauseReasons();
      setPauseReasons(Array.isArray(reasons) ? reasons : []);
    } catch (e) {
      console.error('Erro ao buscar motivos de pausa:', e);
    }
  }, []);

  useEffect(() => {
    fetchQueues();
    loadPauseReasons();
  }, [fetchQueues, loadPauseReasons]);

  useEffect(() => {
    if (activeTab === 'calls') {
      fetchCalls();
    } else if (activeTab === 'pauses') {
      fetchPauses();
    } else {
      fetchMetrics();
    }
  }, [activeTab, fetchCalls, fetchMetrics, fetchPauses]);

  const fetchPausesRef = useRef(fetchPauses);
  useEffect(() => {
    fetchPausesRef.current = fetchPauses;
  }, [fetchPauses]);

  useEffect(() => {
    if (activeTab !== 'pauses') return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') fetchPausesRef.current(true);
    };
    const timer = setInterval(refreshIfVisible, PAUSES_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [activeTab]);

  const pauseSummary = useMemo(() => {
    const byReason = new Map();
    for (const row of pauses) {
      const acc = byReason.get(row.reason_label) || {
        reason_label: row.reason_label,
        reason_color: row.reason_color,
        pause_count: 0,
        total_pause_sec: 0,
      };
      acc.pause_count += toNum(row.pause_count);
      acc.total_pause_sec += toNum(row.total_pause_sec);
      if (!acc.reason_color && row.reason_color) acc.reason_color = row.reason_color;
      byReason.set(row.reason_label, acc);
    }
    return Array.from(byReason.values()).sort((a, b) => b.total_pause_sec - a.total_pause_sec);
  }, [pauses]);

  const pauseAgentTotals = useMemo(() => {
    const totals = new Map();
    for (const row of pauses) {
      totals.set(row.agent_key, (totals.get(row.agent_key) || 0) + toNum(row.total_pause_sec));
    }
    return totals;
  }, [pauses]);

  const effectivePrefix = useMemo(() => {
    if (tenantPrefix) return tenantPrefix;
    const names = [
      ...queues.map((q) => q.name).filter(Boolean),
      ...calls.map((c) => c.queue_name).filter(Boolean),
    ];
    return computeQueuePrefix(tenantId, names) || null;
  }, [tenantPrefix, queues, calls, tenantId]);

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Relatórios</h1>
          <p>Histórico de chamadas e métricas do Call Center</p>
        </div>
      </div>

      <div className="ccv2-tabs">
        <button
          className={`ccv2-tab ${activeTab === 'calls' ? 'active' : ''}`}
          onClick={() => setActiveTab('calls')}
        >
          Chamadas
        </button>
        <button
          className={`ccv2-tab ${activeTab === 'queues' ? 'active' : ''}`}
          onClick={() => setActiveTab('queues')}
        >
          Métricas por Fila
        </button>
        <button
          className={`ccv2-tab ${activeTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveTab('agents')}
        >
          Métricas por Agente
        </button>
        <button
          className={`ccv2-tab ${activeTab === 'pauses' ? 'active' : ''}`}
          onClick={() => setActiveTab('pauses')}
        >
          Pausas
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: '#fde8e8',
            color: '#9b1c1c',
            border: '1px solid #f8b4b4',
            borderRadius: '6px',
            padding: '10px 14px',
            margin: '0 0 12px',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {activeTab === 'calls' && (
        <>
          <div className="ccv2-filters">
            <PeriodFilters
              period={period}
              startDate={filters.startDate}
              endDate={filters.endDate}
              onPeriodChange={applyPeriod}
              onDateChange={setDateField}
            />
            <div className="ccv2-filter-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="answered">Atendidas</option>
                <option value="abandoned">Abandonadas</option>
                <option value="short_abandon">Abandono curto</option>
                <option value="noanswer">Não Atendidas</option>
              </select>
            </div>
            <button className="ccv2-btn ccv2-btn-primary" onClick={fetchCalls}>
              Buscar
            </button>
          </div>

          <div className="ccv2-config-card">
            {loading ? (
              <div className="ccv2-config-loading">
                <div className="ccv2-spinner"></div>
              </div>
            ) : (
              <>
                {calls.length > 0 && (
                  <div className="ccv2-results-info">
                    <span>Mostrando {calls.length} de {callsTotal} chamada(s)</span>
                    {callsTotal > calls.length && (
                      <span className="ccv2-pagination-info">
                        Refine o período para ver as demais
                      </span>
                    )}
                  </div>
                )}
                <div className="ccv2-config-table-wrapper">
                  <table className="ccv2-config-table">
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Origem</th>
                        <th>Fila</th>
                        <th>Agente</th>
                        <th>Espera</th>
                        <th>Duração</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="ccv2-empty-row">
                            Nenhuma chamada encontrada
                          </td>
                        </tr>
                      ) : (
                        calls.map((call, idx) => (
                          <tr key={call.unique_id || idx}>
                            <td>{formatDateTimeSeconds(call.entered_at)}</td>
                            <td className="ccv2-cell-phone">{call.caller_number || '-'}</td>
                            <td title={call.queue_name || ''}>{displayQueue(call.queue_name, effectivePrefix)}</td>
                            <td>
                              <div>{call.agent_name || '-'}</div>
                              {(() => {
                                const ramal = call.agent_extension || extractExtension(call.agent_interface);
                                if (!ramal && !call.agent_interface) return null;
                                const web = isWebEndpoint(call.agent_interface);
                                return (
                                  <small className="ccv2-cell-secondary" title={call.agent_interface || ''}>
                                    {ramal || call.agent_interface}{web ? ' (WebRTC)' : ''}
                                  </small>
                                );
                              })()}
                            </td>
                            <td>{formatDuration(call.wait_time)}</td>
                            <td>{formatDuration(call.talk_time)}</td>
                            <td>{getDispositionBadge(call.disposition)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'queues' && (
        <>
          <div className="ccv2-filters">
            <PeriodFilters
              period={period}
              startDate={filters.startDate}
              endDate={filters.endDate}
              onPeriodChange={applyPeriod}
              onDateChange={setDateField}
            />
            <button className="ccv2-btn ccv2-btn-primary" onClick={fetchMetrics}>
              Buscar
            </button>
          </div>

        <div className="ccv2-config-card">
          {loading ? (
            <div className="ccv2-config-loading">
              <div className="ccv2-spinner"></div>
            </div>
          ) : (
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Oferecidas</th>
                    <th>Atendidas</th>
                    <th>Não Atendidas</th>
                    <th>Abandonadas</th>
                    <th>ASA</th>
                    <th>AHT</th>
                    <th>SLA</th>
                    <th>Abandono</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.queueMetrics.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="ccv2-empty-row">
                        Nenhuma métrica disponível
                      </td>
                    </tr>
                  ) : (
                    metrics.queueMetrics.map((q, idx) => (
                      <tr key={q.queue_key || idx}>
                        <td className="ccv2-cell-name" title={q.queue_name || ''}>{displayQueue(q.queue_name, effectivePrefix)}</td>
                        <td>{q.calls_offered || 0}</td>
                        <td className="ccv2-cell-success">{q.calls_answered || 0}</td>
                        <td>{q.calls_noanswer || 0}</td>
                        <td className="ccv2-cell-danger">{q.calls_abandoned || 0}</td>
                        <td>{formatDuration(q.asa || 0)}</td>
                        <td>{formatDuration(q.aht || 0)}</td>
                        <td>
                          <span className={`ccv2-metric ${q.service_level >= 80 ? 'good' : q.service_level >= 60 ? 'warn' : 'bad'}`}>
                            {(q.service_level || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td>
                          <span className={`ccv2-metric ${q.abandon_rate <= 5 ? 'good' : q.abandon_rate <= 10 ? 'warn' : 'bad'}`}>
                            {(q.abandon_rate || 0).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      )}

      {activeTab === 'agents' && (
        <>
          <div className="ccv2-filters">
            <PeriodFilters
              period={period}
              startDate={filters.startDate}
              endDate={filters.endDate}
              onPeriodChange={applyPeriod}
              onDateChange={setDateField}
            />
            <button className="ccv2-btn ccv2-btn-primary" onClick={fetchMetrics}>
              Buscar
            </button>
          </div>

        <div className="ccv2-config-card">
          {loading ? (
            <div className="ccv2-config-loading">
              <div className="ccv2-spinner"></div>
            </div>
          ) : (
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>Chamadas</th>
                    <th>Tempo Falado</th>
                    <th>AHT</th>
                    <th>Ocupação</th>
                    <th>Tempo Pausa</th>
                    <th>Disponível</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.agentMetrics.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="ccv2-empty-row">
                        Nenhuma métrica disponível
                      </td>
                    </tr>
                  ) : (
                    metrics.agentMetrics.map((a, idx) => (
                      <tr key={a.agent_key || idx}>
                        <td className="ccv2-cell-name">{a.display_name || a.agent_name || a.agent_key}</td>
                        <td>{a.calls_handled || 0}</td>
                        <td>{formatDuration(a.total_talk_time || 0)}</td>
                        <td>{formatDuration(a.aht || 0)}</td>
                        <td>
                          <span className={`ccv2-metric ${a.occupancy >= 70 ? 'good' : a.occupancy >= 50 ? 'warn' : ''}`}>
                            {(a.occupancy || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td>{formatDuration(a.total_pause_time || 0)}</td>
                        <td>{formatDuration(a.total_available_time || 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      )}

      {activeTab === 'pauses' && (
        <>
          <div className="ccv2-filters">
            <PeriodFilters
              period={period}
              startDate={filters.startDate}
              endDate={filters.endDate}
              onPeriodChange={applyPeriod}
              onDateChange={setDateField}
            />
            <div className="ccv2-filter-group">
              <label>Motivo</label>
              <select
                value={filters.reason}
                onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
              >
                <option value="">Todos</option>
                {pauseReasons.map((r) => (
                  <option key={r.code} value={r.code}>{r.label || r.code}</option>
                ))}
              </select>
            </div>
            <div className="ccv2-filter-group">
              <label>Agente</label>
              <input
                type="text"
                placeholder="Ramal"
                value={filters.agent}
                onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
              />
            </div>
            <button className="ccv2-btn ccv2-btn-primary" onClick={fetchPauses}>
              Buscar
            </button>
            <button className="ccv2-btn" onClick={downloadPausesCsv} disabled={pauses.length === 0}>
              Exportar CSV
            </button>
          </div>

          <div className="ccv2-config-card">
            {loading ? (
              <div className="ccv2-config-loading">
                <div className="ccv2-spinner"></div>
              </div>
            ) : (
              <>
                {pauseSummary.length > 0 && (
                  <div className="ccv2-results-info" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {pauseSummary.map((s) => (
                      <span
                        key={s.reason_label}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'rgba(148, 163, 184, 0.12)',
                          borderRadius: '12px',
                          padding: '4px 10px',
                          fontSize: '0.8125rem',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: s.reason_color || '#9ca3af',
                          }}
                        />
                        <strong>{s.reason_label}</strong>
                        <span>{formatDuration(s.total_pause_sec)}</span>
                        <span>({s.pause_count} pausa{s.pause_count === 1 ? '' : 's'})</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="ccv2-config-table-wrapper">
                  <table className="ccv2-config-table">
                    <thead>
                      <tr>
                        <th>Agente</th>
                        <th>Motivo</th>
                        <th>Qtde</th>
                        <th>Tempo Total</th>
                        <th>% Pausa do Agente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pauses.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="ccv2-empty-row">
                            Nenhuma pausa encontrada
                          </td>
                        </tr>
                      ) : (
                        pauses.map((row, idx) => {
                          const prev = idx > 0 ? pauses[idx - 1] : null;
                          const showAgent = !prev || prev.agent_key !== row.agent_key;
                          const agentTotal = pauseAgentTotals.get(row.agent_key) || 0;
                          const pct = agentTotal > 0 ? (toNum(row.total_pause_sec) / agentTotal) * 100 : 0;
                          return (
                            <tr key={`${row.agent_key}-${row.reason_label}-${idx}`}>
                              <td className="ccv2-cell-name">{showAgent ? row.agent_key : ''}</td>
                              <td>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: row.reason_color || '#9ca3af',
                                    marginRight: '6px',
                                  }}
                                />
                                {row.reason_label}
                                {toNum(row.open_pauses) > 0 && (
                                  <span className="ccv2-metric warn" style={{ marginLeft: '6px' }}>
                                    em andamento
                                  </span>
                                )}
                              </td>
                              <td>{toNum(row.pause_count)}</td>
                              <td>{formatDuration(toNum(row.total_pause_sec))}</td>
                              <td>{pct.toFixed(1)}%</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Reports;
